import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  LinearProgress, Chip, CircularProgress, TextField, InputAdornment,
} from '@mui/material';
import { Search, Warning } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import api from '../../api/axios';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

function UsageBar({ used, max }) {
  if (!max || max > 9000) return <Typography variant="caption" color="text.secondary">Unlimited</Typography>;
  const pct = Math.min(100, (used / max) * 100);
  return (
    <Box sx={{ minWidth: 100 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Typography variant="caption">{used}/{max}</Typography>
        <Typography variant="caption" color={pct > 90 ? 'error.main' : 'text.secondary'}>{Math.round(pct)}%</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} color={pct > 90 ? 'error' : pct > 70 ? 'warning' : 'primary'} sx={{ height: 6, borderRadius: 3 }} />
    </Box>
  );
}

export default function UsagePage() {
  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/superadmin/usage')
      .then(r => { setRows(r.data.data); setFiltered(r.data.data); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!search) { setFiltered(rows); return; }
    setFiltered(rows.filter(r => r.name.toLowerCase().includes(search.toLowerCase()) || r.email.toLowerCase().includes(search.toLowerCase())));
  }, [search, rows]);

  const statusColors = { active: 'success', trial: 'info', suspended: 'error', expired: 'default' };

  const overLimit = rows.filter(r =>
    (r.max_users && r.used_users / r.max_users > 0.9) ||
    (r.max_products && r.used_products / r.max_products > 0.9)
  );

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={1}>Usage Monitoring</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>Track resource usage across all tenants</Typography>

      {overLimit.length > 0 && (
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'error.light', bgcolor: 'error.lighter' }}>
          <CardContent>
            <Typography variant="subtitle2" color="error.main" fontWeight={700} mb={1}>
              <Warning sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              {overLimit.length} tenant(s) near or over limit
            </Typography>
            {overLimit.map(t => (
              <Typography key={t.id} variant="body2">{t.name} — check users/products</Typography>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <TextField placeholder="Search tenants..." size="small" value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ mb: 2, width: 280 }} />

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>Tenant</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Branches</TableCell>
                <TableCell>Products</TableCell>
                <TableCell>Customers</TableCell>
                <TableCell align="right">Total Sales</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.email}</Typography>
                  </TableCell>
                  <TableCell><Chip label={r.status} size="small" color={statusColors[r.status] || 'default'} /></TableCell>
                  <TableCell><UsageBar used={r.used_users} max={r.max_users} /></TableCell>
                  <TableCell><UsageBar used={r.used_branches} max={r.max_branches} /></TableCell>
                  <TableCell><UsageBar used={r.used_products} max={r.max_products} /></TableCell>
                  <TableCell><UsageBar used={r.used_customers} max={r.max_customers} /></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight={600}>{fmt(r.total_sales)}</Typography></TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>No tenants found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
}
